/**
 * Created by vladislav on 2/9/2024
 */

/**
 *
 * Platform manager
 *
 * ## Get User's ID on the platform
 * If a player is logged in, **getUserID** returns the player's social ID. If the player is not logged in, a temporary ID is generated.
 * Upon login, the temporary ID is replaced with a real one.
 * ```
 * connector.platform.getUserID();
 * ```
 *
 * ## Platforms with native loading screens
 *
 * <ul class="platforms-list">
 *   <li>
 *       ✅ <img src="images/instant.png"> Facebook Instant Games
 *   </li>
 *   <li>
 *       ✅ <img src="images/samsung.png"> Samsung
 *   </li>
 * </ul>
 *
 *
 * Some platforms have their own loading screens, so you need to report your game's loading progress.
 * ```
 * if (connector.platform.hasNativeLoadingScreen) {
 *   connector.platform.reportLoadingProgress(10);
 *   connector.platform.reportLoadingProgress(50);
 *   connector.platform.reportLoadingProgress(100);
 * }
 * ```
 *
 * ## Notify platforms when your game has loaded and ready to start
 *
 * <ul class="platforms-list">
 *   <li>
 *       ✅ <img src="images/samsung.png"> Samsung
 *   </li>
 *   <li>
 *       ✅ <img src="images/instant.png"> Facebook Instant Games
 *   </li>
 *   <li>
 *       ✅ <img src="images/crazy.png"> Crazy Games
 *   </li>
 *   <li>
 *       ✅ <img src="images/gamesnack.png"> GameSnacks
 *   </li>
 *   <li>
 *       ✅ <img src="images/youtube.png"> YouTube
 *   </li>
 * </ul>
 *
 * ```
 * await connector.platform.startGame();
 * ```
 *
 * ## Notify platforms when your game is interactable
 *
 * <ul class="platforms-list">
 *   <li>
 *       ✅ <img src="images/yandex.png"> Yandex Games
 *   </li>
 *   <li>
 *       ✅ <img src="images/youtube.png"> YouTube
 *   </li>
 *   <li>
 *       ✅ <img src="images/gamesnack.png"> GameSnacks
 *   </li>
 * </ul>
 *
 * On some platforms you need to also notify them when your game is fully interactable and playable.
 *
 * ```
 * connector.platform.notifyGameReady();
 * ```
 *
 * ## Pause
 *
 * You have access to the pause control mechanism in the game.
 * The SDK has a "Paused" flag, by itself it means nothing, however, you can engage in it if you need to pause and continue the game.
 * Additionally, the SDK will notify you when a pause or resume has been triggered.
 *
 * ```
 * connector.platform.isPaused;
 *
 * // Pause the game
 * connector.platform.pause();
 * // Resume the game
 * connector.platform.resume();
 *
 * // Paused the game
 * connector.platform.on("pause", () => {});
 * // Resumed the game
 * connector.platform.on("resume", () => {});
 *
 * // Is the page in the background right now - a pause can also come from the platform sdk,
 * // so it does not by itself mean the page is hidden
 * connector.platform.isPageHidden();
 * ```
 *
 * ## Window focus
 *
 * On desktop, switching to another application or an embedded iframe (an ad, a payment form)
 * only blurs the window - the page stays visible and no pause is triggered. The SDK reports
 * these transitions as separate events. Listeners are expected to mute audio or similar,
 * not to pause the game loop. Not fired on mobile - an app switch there arrives as pause/resume.
 *
 * ```
 * connector.platform.on("windowBlur", () => {});
 * connector.platform.on("windowFocus", () => {});
 * ```
 *
 * ## Review
 *
 * On some platforms, a dialog window may appear prompting the user to rate the game.
 *
 * ```
 * if (connector.platform.canReview()) {
 *  connector.platform.requestReview()
 * }
 * ```
 *
 * ## Platforms with review dialogue window
 *
 * <ul class="platforms-list">
 *   <li>
 *       ✅ <img src="images/microsoft.png"> Microsoft Store
 *   </li>
 *   <li>
 *       ✅ <img src="images/android.png"> Android
 *   </li>
 *   <li>
 *       ✅ <img src="images/mygames.png"> Mygames
 *   </li>
 *   <li>
 *       ✅ <img src="images/ok.png"> OK Games
 *   </li>
 *   <li>
 *       ✅ <img src="images/yandex.png"> Yandex Games
 *   </li>
 * </ul>
 *
 * @namespace Platform
 * */

// appID
//     :
//     "192768"
// coverURL
//     :
//     "https://avatars.mds.yandex.net/get-games/10152950/2a0000018c0143ed281c96617d7c5108c003/cover1"
// iconURL
//     :
//     "https://avatars.mds.yandex.net/get-games/1881957/2a0000018b18aff624590317b53539b1925c/icon1"
// title
//     :
//     "Рейчел Холмс"
// url
//     :
//     "https://yandex.ru/games/app/192768"
/**
 * @typedef GameInfo
 * @memberof Platform
 * @property {string} appID Game's ID on this platform
 * @property {string} imageURL URL to the game's image
 * @property {string} iconURL URL to the game's icon
 * @property {string} title The name of the game
 * @property {string} link Link to the game
 */

const PlatformInterface = function () {
    return Object.assign({
        /**
         * @type {Boolean}
         * @memberof Platform
         * @desc Game pauses when page loses focus or app is minimized. You can also pause it with connector.platform.pause().
         * */
        get isPaused() {
            return this.getPlugin().isPaused();
        },
        /**
         * @type {Boolean}
         * @memberof Platform
         * @desc Indicates whether the platform can control inGame audio by disabling or enabling it.
         * */
        get isAudioEnabled() {
            return this.getPlugin().isAudioEnabled();
        },
        /**
         * @type {Boolean}
         * @memberof Platform
         * @desc If the platform has a dedicated audio control switch, indicates whether audio is currently enabled
         * */
        get hasAudioControls() {
            return this.getPlugin().hasAudioControls();
        },
        /**
         * @type {Boolean}
         * @memberof Platform
         * @desc Whether the platform has its own loading screen, and you need to report your game's loading progress
         * */
        get hasNativeLoadingScreen() {
            return Boolean(this.getPlugin() && this.getPlugin().reportLoadingProgress);
        }
    }, CommonInterface(), {
        _detectPlugin: PlatformDetector,

        /**
         * @function getUserID
         * @memberof Platform
         * @desc Returns userID
         * @return {string}
         * */
        getUserID: function () {
            return this.getPlugin().getUserID();
        },

        setUserID: function (userID) {
            this.getPlugin().setUserID(userID);
        },

        withTmpID: function () {
            const userID = connector.platform.getUserID();
            if (userID) {
                return userID.startsWith("__");
            }

            return false;
        },

        withDeviceID: function () {
            const userID = connector.platform.getUserID();

            const deviceId = connector.info.deviceID;

            if (deviceId) {
                return userID === deviceId;
            }

            return false;
        },

        /**
         * @function listShareChannels
         * @memberof Social
         * @desc Get a list of available share channels
         * @return {Array<string>}
         * */
        listShareChannels: function () {
            return connector._socialManager.listShareChannels();
        },

        /**
         * @function isShareCheckBoxAvailable
         * @memberof Social
         * @desc Whether a window may offer sharing through a checkbox on close
         * @return {Boolean}
         * */
        isShareCheckBoxAvailable: function () {
            return this.getPlugin().isShareCheckBoxAvailable();
        },

        /**
         * @function share
         * @memberof Social
         * @desc Opens share dialog
         * @param {object} options - Options
         * @param {string} options.title - Title
         * @param {string} options.text - Text
         * @param {string} options.image - Image URL
         * @param {string} options.channel - Share channel - "nativeShare", "share", "invite"
         * @param {object} options.linktext - Text for link, for Plinga only
         * @param {object} options.subTitle - Sub title, for Draugiem only
         * @return {Promise<Boolean>}
         * */
        share: function (options) {
            return new Promise((resolve) => {
                this.shareCallback(options, resolve);
            });
        },

        shareCallback: function (options, callback) {
            connector._socialManager.share(options, callback);
        },

        /**
         * @function inviteFriends
         * @memberof Platform
         * @desc Invites friends to the game: the platform's own invite where it has one, otherwise one of its share channels
         * @param {object} data - title, text, image, payload (reaches the invited player's parsePayload where the platform carries it)
         * @param {function} callback - called with true when the invite was sent
         * @param {object} [options]
         * @param {function} [options.login] - login(done): signs the player in before a channel that needs it, done(code)
         * */
        inviteFriends: function (data, callback, options) {
            connector._socialManager.inviteFriends(data, callback, options);
        },

        /**
         * @function checkInvitees
         * @memberof Platform
         * @desc Ids of the player's friends who play this game, without the player and without repeats
         * @param {function} callback - callback(code, ids)
         * */
        checkInvitees: function (callback) {
            connector._socialManager.checkInvitees(callback);
        },

        getAccessToken: function () {
            if (connector.platform.withDeviceID()) {
                return this.getPlugin().getAccessToken();
            }

            return connector.social.getPlugin().getAccessToken();
        },

        /**
         * @function pause
         * @memberof Platform
         * @desc Pauses the game
         * */
        pause: function () {
            this.getPlugin().pause();
        },
        /**
         * @function resume
         * @memberof Platform
         * @desc Resume the game
         * */
        resume: function () {
            this.getPlugin().resume();
        },
        /**
         * @function isPageHidden
         * @memberof Platform
         * @desc Is the page in the background right now
         * @return {Boolean}
         * */
        isPageHidden: function () {
            return this.getPlugin().isPageHidden();
        },

        callNative: function (method, options, callback) {
            this.getPlugin().callNative(method, options, callback);
        },

        subscribeNative: function (method, callback) {
            this.getPlugin().nativeEventListeners[method] = callback;
        },

        unsubscribeNative: function (method) {
            delete this.getPlugin().nativeEventListeners[method];
        },
        /**
         * @function isTester
         * @memberof Platform
         * @desc Whether the player is a tester. Testers can be specified in config.testers
         * */
        isTester: function () {
            return this.getPlugin().isTester();
        },
        /**
         * @function openUrl
         * @memberof Platform
         * @desc Opens external url
         * */
        openUrl: function (url) {
            this.getPlugin().openUrl(url);
        },

        hasVibration: function () {
            return this.getPlugin().hasVibration();
        },

        vibrate: function (pattern) {
            this.getPlugin().vibrate(pattern);
        },
        /**
         * @function locationReload
         * @memberof Platform
         * @desc Reloads page
         * */
        locationReload: function () {
            this.getPlugin().locationReload();
        },

        parsePayload: function (callback) {
            this.getPlugin().parsePayload(callback);
        },

        calcPromo: function (callback) {
            this.parsePayload((data) => {
                const promo = data && data.promo;

                if (promo) {
                    callback(promo);
                } else {
                    this.parseOtherWays(callback);
                }
            });
        },

        parseOtherWays: function (callback) {
            this.getPlugin().parseOtherWays(callback);
        },

        hasCloseApplication: function () {
            return this.getPlugin().closeApplication;
        },

        closeApplication: function () {
            if (this.getPlugin().closeApplication) {
                this.getPlugin().closeApplication();
            }
        },

        isFullscreenAvailable: function () {
            return this.getPlugin().isFullscreenAvailable();
        },

        toggleFullScreen: function () {
            if (this.isFullscreenAvailable() && this.getPlugin().toggleFullScreen) {
                this.getPlugin().toggleFullScreen();
            }
        },

        isExitFullscreenAvailable: function () {
            return this.getPlugin().isExitFullscreenAvailable();
        },

        exitFullscreen: function (callback) {
            if (this.isExitFullscreenAvailable() && this.getPlugin().exitFullscreen) {
                this.getPlugin().exitFullscreen(callback);
            }
        },
        /**
         * @function reportLoadingProgress
         * @memberof Platform
         * @desc Updates loading progress on platform loading screen.
         *
         * Platforms: Samsung, Crazy, Instant.
         * @param {number} percent - Loading progress
         * */
        reportLoadingProgress: function (percent) {
            if (this.getPlugin() && this.getPlugin().reportLoadingProgress) {
                this.getPlugin().reportLoadingProgress(percent);
            }
        },
        /**
         * @function startGame
         * @memberof Platform
         * @desc Needs to be called on some platforms when the game finished loading.
         * @return {Promise}
         *
         *
         * Platforms: Samsung, Crazy, Instant, GameSnacks, YouTube.
         * */
        startGame: function () {
            return new Promise((resolve) => {
                this.startGameCallback(resolve);
            });
        },

        startGameCallback: function (callback) {
            if (this.getPlugin().startGame) {
                this.getPlugin().startGame(callback);
            } else {
                callback();
            }
        },

        /**
         * @function getSafePadding
         * @memberof Platform
         * @desc Returns safe screen paddings e.g. { left: 20, right: 0, top: 50, bottom: 0}
         * @return {object}
         *
         * */
        getSafePadding: function () {
            return this.getPlugin().getSafePadding();
        },

        /**
         * @function getFrameSize
         * @memberof Platform
         * @desc Returns window frame size e.g. { width: 1920, height: 1080}
         * @return {object}
         *
         * */
        getFrameSize: function () {
            return this.getPlugin().getFrameSize();
        },

        getLocalStoragePrefix: function () {
            return this.getPlugin().getLocalStoragePrefix();
        },

        /**
         * @function canReview
         * @memberof Platform
         * @desc Whether review is available
         * @return {Boolean}
         * */
        canReview: function () {
            return this.getPlugin().canReview();
        },

        /**
         * @function requestReview
         * @memberof Platform
         * @desc Show a dialog that suggests a user to rate the game
         * @return {Promise<Boolean>}
         * */
        requestReview: function () {
            return new Promise((resolve) => {
                this.requestReviewCallback((code) => {
                    resolve(code === connector.CODE_SUCCEED);
                });
            });
        },

        requestReviewCallback: function (callback) {
            this.getPlugin().requestReview(callback);
        },

        /**
         * @function isLatestVersionCheckSupported
         * @memberof Platform
         * @desc Whether the platform can tell the latest published version of the game
         * @return {Boolean}
         * */
        isLatestVersionCheckSupported: function () {
            return this.getPlugin().isLatestVersionCheckSupported();
        },

        /**
         * @function checkLatestVersion
         * @memberof Platform
         * @desc Ask the platform for the latest published version of the game
         * @param {Function} callback - (code, response) with response.version on success
         * */
        checkLatestVersion: function (callback) {
            this.getPlugin().checkLatestVersion(callback);
        },

        /**
         * @function canCreateShortcut
         * @memberof Platform
         * @desc Whether shortcut is available
         * @return {Boolean}
         * */
        canCreateShortcut: function () {
            return this.getPlugin().canCreateShortcut();
        },

        /**
         * @function canCreateShortcutAsync
         * @memberof Platform
         * @desc Whether shortcut is available
         * @return {Promise<Boolean>}
         * */
        canCreateShortcutAsync: function () {
            return new Promise((resolve) => {
                this.canCreateShortcutCallback((canCreateShortcut) => {
                    resolve(canCreateShortcut);
                });
            });
        },

        canCreateShortcutCallback: function (callback) {
            return this.getPlugin().canCreateShortcutAsync(callback);
        },

        /**
         * @function createShortcut
         * @memberof Platform
         * @desc Create shortcut on home screen or add game to favorites
         * @return {Promise<Boolean>}
         * */
        createShortcut: function () {
            return new Promise((resolve) => {
                this.createShortcutCallback((code) => {
                    resolve(code === connector.CODE_SUCCEED);
                });
            });
        },

        createShortcutCallback: function (callback) {
            this.getPlugin().createShortcut(callback);
        },

        /**
         * @function getShortcutMissionReward
         * @memberof Platform
         * @desc Whether shortcut reward can be given (e.g. user completed shortcut mission)
         * @param {Function} callback - callback(canReceive: Boolean)
         * */
        getShortcutMissionReward: function (callback) {
            this.getPlugin().getShortcutMissionReward(callback);
        },

        /**
         * @function canStartEntranceMission
         * @memberof Platform
         * @desc Whether entrance mission (profile sidebar visit) is available
         * @return {Boolean}
         * */
        canStartEntranceMission: function () {
            return this.getPlugin().canStartEntranceMission();
        },

        /**
         * @function startEntranceMission
         * @memberof Platform
         * @desc Jump to TikTok profile sidebar to guide repeat visits
         * @return {Promise<Boolean>}
         * */
        startEntranceMission: function () {
            return new Promise((resolve) => {
                this.startEntranceMissionCallback((code) => {
                    resolve(code === connector.CODE_SUCCEED);
                });
            });
        },

        startEntranceMissionCallback: function (callback) {
            this.getPlugin().startEntranceMission(callback);
        },

        /**
         * @function getEntranceMissionReward
         * @memberof Platform
         * @desc Whether entrance mission reward can be given (e.g. user completed profile sidebar visit)
         * @param {Function} callback - callback(canReceive: Boolean)
         * */
        getEntranceMissionReward: function (callback) {
            this.getPlugin().getEntranceMissionReward(callback);
        },

        /**
         * @function notifyGameReady
         * @memberof Platform
         * @desc Needs to be called on some platforms when the game is ready and playable.
         *
         * Platforms: Yandex, YouTube.
         * */
        notifyGameReady: function () {
            if (this.getPlugin().notifyGameReady && !this.getPlugin().notifyGameReadyDone) {
                this.getPlugin().notifyGameReadyDone = true;

                connector.platform.whenConnected(() => {
                    this.getPlugin().notifyGameReady();
                });
            }
        },

        gameplayStartCounter: 0,

        /**
         * @function isGameplayActive
         * @memberof Platform
         * @desc Whether gameplay is currently active, i.e. every gameplayStop has been given back by a gameplayStart.
         * @return {Boolean}
         * */
        isGameplayActive: function () {
            const plugin = this.getPlugin();
            return this.gameplayStartCounter > 0 || !plugin.gameplayStop;
        },

        /**
         * @function gameplayStart
         * @memberof Platform
         * @desc Has to be called whenever the player starts playing or resumes playing after a break (menu/loading/achievement screen, game paused, etc.)
         * Counts starts minus stops: the platform is told gameplay started only when the counter goes 0 -> 1.
         *
         * Platforms: Crazy, Arkadium.
         * */
        gameplayStart: function (levelNo) {
            const plugin = this.getPlugin();
            if (!plugin.gameplayStart) {
                return;
            }

            this.gameplayStartCounter++;

            if (this.gameplayStartCounter === 1) {
                plugin.gameplayStart(levelNo);
                plugin.bus.trigger("gameplayStart");
            }
        },

        /**
         * @function gameplayStop
         * @memberof Platform
         * @desc Has to be called on every game break (entering a menu, switching level, pausing the game, etc.)
         * Every call has to be paired with a gameplayStart: breaks may overlap (a window over a hint),
         * and gameplay resumes only when the last of them is over. The counter goes negative while a break
         * is shown outside gameplay (outcome window, map, loading) and comes back to 0 on its own.
         *
         * Platforms: Crazy, Arkadium.
         * */
        gameplayStop: function (levelNo) {
            const plugin = this.getPlugin();
            if (!plugin.gameplayStop) {
                return;
            }

            this.gameplayStartCounter--;

            if (this.gameplayStartCounter === 0) {
                plugin.gameplayStop(levelNo);
                plugin.bus.trigger("gameplayStop");
            }
        },

        /**
         * @function listGames
         * @memberof Platform
         * @desc Get as list with information about all your games published on this platform
         * @return {Promise<Array<Platform.GameInfo>>}
         *
         * Platforms: Yandex.
         * */
        listGames: function () {
            const plugin = this.getPlugin();
            if (plugin.listGames && plugin.isConnected()) {
                return plugin.listGames();
            }

            return Promise.resolve([]);
        },

        /**
         * @function canPassPayload
         * @memberof Platform
         * @desc Whether the payload given to switchGame reaches the target game's parsePayload
         * @return {Boolean}
         * */
        canPassPayload: function () {
            return this.getPlugin().canPassPayload();
        },

        /**
         * @function hasPublisherPlayerId
         * @memberof Platform
         * @desc Whether the social user id is the publisher's player id - the same for one player in every game of the publisher, so two games can match the player without a payload
         * @return {Boolean}
         *
         * Platforms: MS Start (signed in).
         * */
        hasPublisherPlayerId: function () {
            return this.getPlugin().hasPublisherPlayerId();
        },

        /**
         * @function canSwitchGame
         * @memberof Platform
         * @desc Whether switchGame can take the player to another game of the publisher here
         * @return {Boolean}
         * */
        canSwitchGame: function () {
            return this.getPlugin().canSwitchGame();
        },

        switchGame: function (url, payload, callback) {
            this.getPlugin().switchGame(url, payload, callback);
        },

        getGameUrl: function (source) {
            return Platform.getGameUrl(source);
        },

        getRateUrl: function () {
            return Platform.getRateUrl();
        },

        getExternalUrl: function (url) {
            return Platform.getExternalUrl(url);
        }
    });
};